import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Card } from "react-bootstrap";
import { ServerPath } from "../../Constant";
import { _axios } from "../../utils/axios";
import { toast } from "react-toastify";

const LANGUAGES = [
  { code: "en", name: "English", titleField: "title" },
  { code: "hi", name: "Hindi", titleField: "title_hin" },
  { code: "mr", name: "Marathi", titleField: "title_mar" },
  { code: "gu", name: "Gujarati", titleField: "title_guj" },
];

const COURSE_LANGUAGE_TO_CODE = {
  English: "en",
  Hindi: "hi",
  Marathi: "mr",
  Gujarati: "gu",
};

const SelectLessonModal = ({
  show,
  onHide,
  onSelect,
  selectedArtifactId,
  courseType,
  courseLanguage,
}) => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(
    selectedArtifactId || null
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    COURSE_LANGUAGE_TO_CODE[courseLanguage] || "en"
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (show) {
      fetchArtifacts();
      setSelectedLanguage(COURSE_LANGUAGE_TO_CODE[courseLanguage] || "en");
      setSearchQuery("");
    }
  }, [show, courseType, courseLanguage]);

  const getArtifactTypesByCoursetype = (courseType) => {
    switch (courseType) {
      case "Video Course":
        return ["LONG_VIDEOS"];
      case "Audio":
        return ["AUDIO_ORALS"];
      case "Text-Based Course":
        return ["TEXT_LITERATURE"];
      default:
        return [];
    }
  };

  const fetchArtifacts = async () => {
    try {
      setLoading(true);

      const artifactTypes = getArtifactTypesByCoursetype(courseType);

      const promises = artifactTypes.map((type) =>
        _axios.get(`${ServerPath}/artifacts`, {
          params: {
            type: type,
            is_a_part_of_course: 1,
            status: "ACTIVE",
          },
        })
      );

      const responses = await Promise.all(promises);

      let allArtifacts = [];

      responses.forEach((response) => {
        if (response.data.success && response.data.data) {
          allArtifacts = [...allArtifacts, ...response.data.data];
        }
      });

      setArtifacts(allArtifacts);
    } catch (error) {
      console.error("Error fetching artifacts:", error);
      setArtifacts([]);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (artifact) => {
    const lang = LANGUAGES.find((l) => l.code === selectedLanguage);
    return (lang && artifact[lang.titleField]) || artifact.title;
  };

  const filteredArtifacts = artifacts.filter((a) => {
    const matchesLanguage = a.lang_flag?.split(",").map((s) => s.trim()).includes(selectedLanguage);
    const matchesSearch = searchQuery.trim() === "" || (a.title || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesLanguage && matchesSearch;
  });

  const handleSelect = (artifact) => {
    setSelectedItemId(artifact.artifact_id);
    onSelect(artifact);
  };

const handlePreview = (artifact) => {

    let url =
        artifact[`media_url_${artifact.lang_flag}`] ||
        artifact.media_url ||
        artifact.media_url_hin ||
        artifact.media_url_mar ||
        artifact.media_url_guj;

    console.log("Preview artifact:", artifact);
    console.log("Preview URL/HTML:", url);

    setPreviewUrl(url);
    setShowPreview(true);
};

  const processHtmlForPreview = (html) => {
    if (!html) return "<p>No preview available</p>";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const olElements = tempDiv.querySelectorAll("ol");
    olElements.forEach((ol) => {
        ol.style.paddingLeft = "20px";
        ol.style.margin = "10px 0";
        ol.style.listStyleType = "decimal";
    });

    const ulElements = tempDiv.querySelectorAll("ul");
    ulElements.forEach((ul) => {
        ul.style.paddingLeft = "20px";
        ul.style.margin = "10px 0";
        ul.style.listStyleType = "disc";
    });

    const liElements = tempDiv.querySelectorAll("li");
    liElements.forEach((li) => {
        li.style.margin = "5px 0";
        li.style.lineHeight = "1.6";
    });

    return tempDiv.innerHTML;
};

const renderPreview = () => {
    if (!previewUrl) return null;

    const isVideo = previewUrl.match(/\.(mp4|webm|ogg)$/i);
    const isAudio = previewUrl.match(/\.(mp3|wav|ogg)$/i);

    if (isVideo) {
        return (
            <video controls style={{ width: "100%", maxHeight: "450px" }}>
                <source src={previewUrl} />
            </video>
        );
    }

    if (isAudio) {
        return (
            <audio controls style={{ width: "100%" }}>
                <source src={previewUrl} />
            </audio>
        );
    }

    // TEXT_LITERATURE preview
    if (courseType === "Text-Based Course") {
        return (
            <div
                style={{
                    padding: "20px",
                    maxHeight: "500px",
                    overflowY: "auto",
                    background: "#fff",
                    borderRadius: "8px",
                    lineHeight: "1.6",
                    fontSize: "16px"
                }}
                dangerouslySetInnerHTML={{
                    __html: processHtmlForPreview(previewUrl)
                }}
            />
        );
    }

    return (
        <iframe
            src={previewUrl}
            style={{ width: "100%", height: "450px" }}
            title="preview"
        />
    );
};

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Select Lessons
            {courseType && (
              <span className="text-muted ms-2">({courseType})</span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {/* Language Filter */}
          <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
            <span className="fw-semibold text-secondary">Language:</span>
            {LANGUAGES.map((lang) => (
              <label
                key={lang.code}
                className="d-flex align-items-center gap-1"
                style={{ cursor: "pointer", fontWeight: selectedLanguage === lang.code ? "600" : "400" }}
              >
                <input
                  type="radio"
                  name="artifactLanguage"
                  value={lang.code}
                  checked={selectedLanguage === lang.code}
                  onChange={() => setSelectedLanguage(lang.code)}
                  style={{ width: "16px", height: "16px" }}
                />
                {lang.name}
              </label>
            ))}
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search artifacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ maxWidth: "360px" }}
            />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <p className="mt-3">Loading artifacts...</p>
            </div>
          ) : filteredArtifacts.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">
                No artifacts available for {courseType} in {LANGUAGES.find((l) => l.code === selectedLanguage)?.name}
              </p>
            </div>
          ) : (
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {filteredArtifacts.map((artifact) => (
                <Col key={artifact.artifact_id}>
                  <Card
                    className="h-100 shadow-sm"
                    style={{
                      borderRadius: "10px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Radio */}
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        zIndex: 2,
                      }}
                    >
                      <input
                        type="radio"
                        name="selectArtifact"
                        checked={selectedItemId === artifact.artifact_id}
                        onChange={() =>
                          setSelectedItemId(artifact.artifact_id)
                        }
                        style={{ width: "18px", height: "18px" }}
                      />
                    </div>

                    {/* Thumbnail */}
                    <div
                      style={{
                        height: "170px",
                        backgroundImage: `url(${
                          artifact.thumbnail_url ||
                          `https://via.placeholder.com/300x170`
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />

                    {/* Title */}
                    <div
                      style={{
                        padding: "10px",
                        minHeight: "55px",
                        fontSize: "14px",
                        fontWeight: "500",
                        textAlign: "center",
                      }}
                    >
                      {getTitle(artifact)}
                    </div>

                    {/* Buttons */}
                    <div
                      style={{
                        marginTop: "auto",
                        display: "flex",
                      }}
                    >
                      <Button
                        variant="primary"
                        className="flex-grow-1 rounded-0"
                        disabled={selectedItemId !== artifact.artifact_id}
                        onClick={() => handleSelect(artifact)}
                        style={{
                          background: "#6366F1",
                          border: "none",
                        }}
                      >
                        Select
                      </Button>

                      <Button
                        variant="warning"
                        className="flex-grow-1 rounded-0"
                        onClick={() => handlePreview(artifact)}
                        style={{
                          background: "#F59E0B",
                          border: "none",
                        }}
                      >
                        Preview
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Content Preview</Modal.Title>
        </Modal.Header>

        <Modal.Body>{renderPreview()}</Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectLessonModal;